import React, { useEffect, useState } from "react";
import useHttp from "../../Hooks/use-http";
import { FileUpload as FileUploadIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

const VehicleRegistrationDocument = ({
  setSnackbarOpen,
  setSnackbarMessage,
  isUploading,
  setIsUploading,
  emailID,
  documents,
  setDocuments,
  vehicleCustom,
  vehicleNumber,
}) => {
  const [isUpload, setIsUpload] = useState(false);
  const { sendRequest, isLoading } = useHttp();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await sendRequest({
          url: "/documentsmaster",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailID: localStorage.getItem("user"),
          },
        });

        if (data?.status === 200 || data?.status === 201) {
          const filteredDocuments =
            data?.documentMaster.filter(
              (doc) =>
                doc.category === "Vehicle" ||
                (vehicleCustom && doc.category === "Partner")
            ) || [];
          const newDocuments = filteredDocuments.map((doc) => ({
            name: doc.documentName || "",
            type: doc.type,
            documentID: doc.documentID,
            number: "",
            category: doc.category,
            expiryDate: "",
            file: null,
            url: "",
            error: "",
            preview: "",
            isUploaded: false,
            required: doc.required,
            hasExpiry: doc.hasExpiry,
          }));
          setDocuments(newDocuments);
        }
      } catch (error) {
        console.error("Error fetching document master", error);
        setSnackbarMessage("Error loading document requirements");
        setSnackbarOpen(true);
      }
    };

    fetchData();
  }, [sendRequest, vehicleCustom]);

  const documentChangeHandler = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : file.name;

      setDocuments((prev) => {
        const updatedDocuments = [...prev];
        updatedDocuments[index] = {
          ...updatedDocuments[index],
          file: file,
          preview: preview,
          error: "",
        };
        return updatedDocuments;
      });
    }
  };

  const uploadFileToBackend = async (index) => {
    if (!documents[index].file) {
      setSnackbarMessage("Please select a file first");
      setSnackbarOpen(true);
      return;
    }

    setIsUploading(true);
    const file = documents[index].file;
    const FolderName = "vehicle";
    const uniqueID = vehicleNumber || emailID;
    const documentID = documents[index].documentID;

    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file?.name ?? "random");
      formData.append("folderName", FolderName);
      formData.append("uniqueID", uniqueID);
      formData.append("documentID", documentID);
      formData.append("fileExtension", fileExtension);

      const response = await fetch(
        "https://littleinfinity.in:4001/documentfileupload",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (response.ok) {
        const fileUrl = data?.data;
        setDocuments((prev) => {
          const updatedDocuments = [...prev];
          updatedDocuments[index] = {
            ...updatedDocuments[index],
            url: fileUrl,
            isUploaded: true,
          };
          return updatedDocuments;
        });
        setSnackbarMessage(`${documents[index].name} uploaded successfully!`);
      } else {
        throw new Error(data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setSnackbarMessage(
        `Error uploading ${documents[index].name}: ${error.message}`
      );
    } finally {
      setIsUploading(false);
      setSnackbarOpen(true);
    }
  };

  const handleDocumentNumberChange = (index, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index].number = value;
    setDocuments(updatedDocuments);
  };

  const handleExpiryDateChange = (index, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index].expiryDate = value;
    setDocuments(updatedDocuments);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Vehicle Documents
      </Typography>

      <Grid container spacing={3}>
        {documents.map((doc, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: doc.isUploaded ? "#f0f9f0" : "#fafafa",
                transition: "all 0.3s ease",
                border: doc.isUploaded
                  ? "1px solid #4caf50"
                  : "1px solid #e0e0e0",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {doc.name}
                    {doc.required && (
                      <span style={{ color: "red", marginLeft: 4 }}>*</span>
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Document Number"
                    value={doc.number}
                    onChange={(e) =>
                      handleDocumentNumberChange(index, e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    disabled={doc.isUploaded}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  {doc.hasExpiry && (
                    <TextField
                      fullWidth
                      label="Expiry Date"
                      type="date"
                      value={doc.expiryDate}
                      onChange={(e) =>
                        handleExpiryDateChange(index, e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      disabled={doc.isUploaded}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={2}>
                  {!doc.isUploaded ? (
                    <>
                      <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        startIcon={<FileUploadIcon />}
                        disabled={isUploading}
                        sx={{ mb: 1 }}
                      >
                        {doc.preview ? "Change" : "Select"}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => documentChangeHandler(index, e)}
                          accept={doc.type === "image" ? "image/*" : "*"}
                        />
                      </Button>

                      {doc.preview && (
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={() => uploadFileToBackend(index)}
                          disabled={isUploading || !doc.file}
                          startIcon={
                            isUploading ? <CircularProgress size={20} /> : null
                          }
                        >
                          {isUploading ? "Uploading..." : "Upload"}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight={500}
                      >
                        Uploaded
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {doc.preview && !doc.isUploaded && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {doc.preview.startsWith("blob:") ? (
                        <img
                          src={doc.preview}
                          alt="Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "150px",
                            objectFit: "contain",
                          }}
                          onLoad={() => URL.revokeObjectURL(doc.preview)}
                        />
                      ) : (
                        <Typography variant="body2">{doc.preview}</Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default VehicleRegistrationDocument;
